import apiClient from '../apiClient';
import {
    Notification,
    NotificationStats,
    NotificationType,
    NotificationPriority,
    NotificationCategory,
    NotificationStatus,
} from '@/types/notification';
import { MultilingualField } from '@/types/localization';

// ==================== Request DTOs ====================

export interface GetNotificationsParams {
    page?: number;
    limit?: number;
    type?: NotificationType;
    priority?: NotificationPriority;
    category?: NotificationCategory;
    status?: NotificationStatus;
    unreadOnly?: boolean;
    fromDate?: string;
    toDate?: string;
}

export interface CreateNotificationDto {
    type: NotificationType;
    priority: NotificationPriority;
    category: NotificationCategory;
    title: MultilingualField;
    message: MultilingualField;
    icon?: string;
    actionUrl?: string;
    actionText?: MultilingualField;
    userId?: string;
    patientId?: string;
    patientName?: string;
    orderId?: string;
    appointmentId?: string;
    requiresAcknowledgment?: boolean;
    expiresAt?: string;
    metadata?: Record<string, unknown>;
    deliveryChannels?: string[];
}

// ==================== Response DTOs ====================

export interface NotificationsResponse {
    success: boolean;
    data: Notification[];
    meta: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
}

export interface UnreadCountResponse {
    success: boolean;
    data: {
        count: number;
    };
}

export interface NotificationResponse {
    success: boolean;
    data: Notification;
}

export interface MessageResponse {
    success: boolean;
    message: string;
}

// ==================== API Service ====================

export const notificationsApi = {
    /**
     * Get notifications with filters and pagination
     * GET /notifications
     */
    async getNotifications(params: GetNotificationsParams = {}): Promise<NotificationsResponse> {
        const response = await apiClient.get<NotificationsResponse>('/notifications', { params });
        return response.data;
    },

    /**
     * Get unread notifications count
     * GET /notifications/unread-count
     */
    async getUnreadCount(): Promise<number> {
        const response = await apiClient.get<UnreadCountResponse>('/notifications/unread-count');
        return response.data.data.count;
    },

    /**
     * Get notification statistics
     * GET /notifications/stats
     */
    async getStatistics(): Promise<NotificationStats> {
        const response = await apiClient.get<{ success: boolean; data: NotificationStats }>('/notifications/counts');
        return response.data.data;
    },

    /**
     * Get a single notification by ID
     * GET /notifications/:id
     */
    async getNotification(id: string): Promise<Notification> {
        const response = await apiClient.get<NotificationResponse>(`/notifications/${id}`);
        return response.data.data;
    },

    /**
     * Mark a notification as read
     * PUT /notifications/:id/read
     */
    async markAsRead(id: string): Promise<Notification> {
        const response = await apiClient.put<NotificationResponse>(`/notifications/${id}/read`);
        return response.data.data;
    },

    /**
     * Mark all notifications as read
     * PUT /notifications/mark-all-read
     */
    async markAllAsRead(): Promise<void> {
        await apiClient.put<MessageResponse>('/notifications/mark-all-read');
    },

    /**
     * Archive a notification
     * PUT /notifications/:id/archive
     */
    async archiveNotification(id: string): Promise<Notification> {
        const response = await apiClient.put<NotificationResponse>(`/notifications/${id}/archive`);
        return response.data.data;
    },

    /**
     * Unarchive a notification
     * PUT /notifications/:id/unarchive
     */
    async unarchiveNotification(id: string): Promise<Notification> {
        const response = await apiClient.put<NotificationResponse>(`/notifications/${id}/unarchive`);
        return response.data.data;
    },

    /**
     * Acknowledge a critical notification
     * PUT /notifications/:id/acknowledge
     */
    async acknowledgeNotification(id: string): Promise<Notification> {
        const response = await apiClient.put<NotificationResponse>(`/notifications/${id}/acknowledge`);
        return response.data.data;
    },

    /**
     * Delete a notification (soft delete)
     * DELETE /notifications/:id
     */
    async deleteNotification(id: string): Promise<void> {
        await apiClient.delete<MessageResponse>(`/notifications/${id}`);
    },

    /**
     * Bulk delete notifications
     * DELETE /notifications/bulk
     */
    async bulkDeleteNotifications(ids: string[]): Promise<void> {
        await apiClient.delete<MessageResponse>('/notifications/bulk', { data: { ids } });
    },

    /**
     * Create a new notification (internal/system use)
     * POST /notifications
     */
    async createNotification(data: CreateNotificationDto): Promise<Notification> {
        const response = await apiClient.post<NotificationResponse>('/notifications', data);
        return response.data.data;
    },
};
